import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import AppHeader from '../../components/shell/AppHeader'
import { supabase } from '../../lib/supabase'
import { getActiveLeaveTypes, cancelLeaveRequest, getLeaveRequest, reopenLeaveRequest, saveLeaveRequest, type LeaveRequest, type LeaveType } from './leaveRequestStore'
import { requestHorizonEndFor } from './requestHorizon'
import { useProfileIdentity } from '../../lib/useProfileIdentity'
import QuarterHourField from '../../components/agent/QuarterHourField'

/**
 * Requesting leave, docs/design/journey-map.md's "Requesting leave": a
 * request that stands on its own, so an agent can ask for leave without
 * resubmitting a whole schedule. Decision-making (approve, return, deny)
 * and its coverage estimate are a supervisor capability, not built yet; a
 * submission here only ever reaches the 'submitted' state.
 *
 * A `reopen` search param, set by the request history page's own Edit
 * action on a pending request or Reopen action on a returned one, switches this same form into editing
 * that request instead of starting a blank one: the fields prefill from
 * it, the supervisor's own return comment shows above them for context,
 * and submitting calls reopenLeaveRequest instead of saveLeaveRequest.
 */

const COMMENT_MAX_LENGTH = 500

function ArrowLeftIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  )
}

// Same calendar glyph as the dashboard's View calendar action, so a date
// field's picker trigger reads as the same affordance everywhere it appears.
function CalendarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  )
}

// The actual current date, same derivation as AgentDashboardPage.tsx's own
// REAL_TODAY_ISO: leave is requested for a day still ahead, never one
// already past. Read once at load; nothing here needs to tick live.
const now = new Date()
const REAL_TODAY_ISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`

const REQUEST_HORIZON_END_ISO = requestHorizonEndFor(REAL_TODAY_ISO)

const REQUEST_HORIZON_END_LABEL = (() => {
  const [year, month, day] = REQUEST_HORIZON_END_ISO.split('-').map(Number)
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
})()

export default function AgentRequestLeavePage() {
  const identity = useProfileIdentity()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const reopenId = searchParams.get('reopen')
  const [userId, setUserId] = useState<string | null>(null)
  const [sessionReady, setSessionReady] = useState(false)
  const [reopenTarget, setReopenTarget] = useState<LeaveRequest | null>(null)
  const [confirmingCancel, setConfirmingCancel] = useState(false)
  const [cancelFailed, setCancelFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (cancelled) return
        setUserId(session?.user.id ?? null)
      })
      .catch(() => {
        // No session to read; the form still renders, and submitting
        // without one is what already reports the failure below.
      })
      .finally(() => {
        if (!cancelled) setSessionReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const startDateInputRef = useRef<HTMLInputElement>(null)
  const endDateInputRef = useRef<HTMLInputElement>(null)

  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [fullDay, setFullDay] = useState(true)
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  // The open leave types, and the one chosen: the first until the agent picks.
  const [openLeaveTypes, setOpenLeaveTypes] = useState<LeaveType[]>([])
  const [chosenLeaveType, setChosenLeaveType] = useState<LeaveType | null>(null)
  const [comment, setComment] = useState('')
  const [submitFailed, setSubmitFailed] = useState(false)

  // Prefills the form from the returned request once the session and the
  // reopen param are both known; a request that is no longer this
  // agent's own or already decided simply never resolves, and the
  // form stays a blank one rather than silently editing something else,
  // the same shape leave_requests_reopen_own's own using clause enforces
  // on the eventual write.
  useEffect(() => {
    if (!sessionReady || !userId || !reopenId) return
    let cancelled = false
    getLeaveRequest(userId, reopenId).then((request) => {
      if (cancelled || !request || (request.state !== 'returned' && request.state !== 'pending')) return
      setReopenTarget(request)
      setStartDate(request.startDate)
      setEndDate(request.endDate)
      setFullDay(request.fullDay)
      setStartTime(request.startTime ?? '')
      setEndTime(request.endTime ?? '')
      setChosenLeaveType(request.leaveType)
      setComment(request.comment ?? '')
    })
    return () => {
      cancelled = true
    }
  }, [sessionReady, userId, reopenId])

  useEffect(() => {
    let cancelled = false
    getActiveLeaveTypes().then((types) => {
      if (!cancelled) setOpenLeaveTypes(types)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // A returned request keeps its own type in the list even once it has been
  // retired, so reopening it never silently changes what it asks for.
  const leaveTypes = reopenTarget && !openLeaveTypes.includes(reopenTarget.leaveType) ? [reopenTarget.leaveType, ...openLeaveTypes] : openLeaveTypes
  const leaveType = chosenLeaveType ?? leaveTypes[0] ?? ''

  const startDateInPast = startDate !== '' && startDate < REAL_TODAY_ISO
  const beyondRequestHorizon =
    (startDate !== '' && startDate > REQUEST_HORIZON_END_ISO) || (endDate !== '' && endDate > REQUEST_HORIZON_END_ISO)
  const datesOrdered = startDate !== '' && endDate !== ''
  const dateRangeInvalid = datesOrdered && endDate < startDate
  const timesOrdered = startTime !== '' && endTime !== ''
  const partialTimesMissing = !fullDay && (startTime === '' || endTime === '')
  const timeRangeInvalid = !fullDay && timesOrdered && endTime <= startTime

  const canSubmit =
    leaveType !== '' &&
    datesOrdered &&
    !startDateInPast &&
    !beyondRequestHorizon &&
    !dateRangeInvalid &&
    (fullDay || (!partialTimesMissing && !timeRangeInvalid))

  async function handleSubmit() {
    if (!canSubmit) return

    if (!userId) {
      setSubmitFailed(true)
      return
    }

    const edited = {
      startDate,
      endDate,
      fullDay,
      startTime: fullDay ? undefined : startTime,
      endTime: fullDay ? undefined : endTime,
      leaveType,
      comment: comment.trim() === '' ? undefined : comment.trim(),
    }
    const submitted = reopenTarget
      ? await reopenLeaveRequest(userId, reopenTarget.id, edited)
      : await saveLeaveRequest(userId, edited)

    if (!submitted) {
      setSubmitFailed(true)
      return
    }

    navigate('/agent', { state: { leaveRequested: true } })
  }

  // Removes a request still awaiting a decision. Reached only from the
  // confirmation below, and refused by the server once a supervisor has
  // decided it, in which case the request history shows where it stands.
  async function handleCancel() {
    if (!userId || !reopenTarget || reopenTarget.state !== 'pending') return
    setCancelFailed(false)
    const cancelled = await cancelLeaveRequest(userId, reopenTarget.id)
    if (!cancelled) {
      setCancelFailed(true)
      return
    }
    navigate('/agent', { state: { leaveCancelled: true } })
  }

  if (!sessionReady) {
    return (
      <div className="mx-auto flex h-dvh w-full max-w-md flex-col border-line bg-canvas text-ink sm:border-x lg:max-w-none lg:border-x-0">
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-4">
          <p role="status" className="text-small text-ink-muted">
            Loading&hellip;
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto flex h-dvh w-full max-w-md flex-col border-line bg-canvas text-ink sm:border-x lg:max-w-none lg:border-x-0">
      <a
        href="#request-leave-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <AppHeader initials={identity?.initials ?? ''} />

      <main id="request-leave-main" tabIndex={0} className="flex flex-1 flex-col overflow-y-auto">
        <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-4 p-4 lg:gap-6 lg:p-8">
          <Link
            to="/agent"
            className="flex min-h-11 items-center gap-1.5 self-start rounded-control text-small font-bold text-ink-muted"
          >
            <ArrowLeftIcon />
            Dashboard
          </Link>

          <div>
            <h1 className="text-h3 font-extrabold lg:text-h2">{reopenTarget ? (reopenTarget.state === 'pending' ? 'Edit Leave Request' : 'Resubmit Leave Request') : 'Request Leave'}</h1>
            <p className="mt-0.5 text-small text-ink-muted">
              {reopenTarget
                ? reopenTarget.state === 'pending'
                  ? 'Update the request below until your supervisor decides it.'
                  : 'Update the request below and resubmit it for another decision.'
                : 'A request stands on its own; it does not need a schedule submission alongside it.'}
            </p>
          </div>

          {reopenTarget?.state === 'returned' && reopenTarget.decisionComment && (
            <div className="rounded-surface border border-line bg-surface-2 p-3">
              <p className="text-caption font-bold text-ink-muted">
                Returned{reopenTarget.decidedByName ? ` by ${reopenTarget.decidedByName}` : ''}
              </p>
              <p className="mt-1 text-small">&ldquo;{reopenTarget.decisionComment}&rdquo;</p>
            </div>
          )}

          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label htmlFor="leave-start-date" className="mb-1 block text-caption font-bold text-ink-muted">
                  Start date
                </label>
                <div className="relative">
                  <input
                    id="leave-start-date"
                    ref={startDateInputRef}
                    type="date"
                    min={REAL_TODAY_ISO}
                    max={REQUEST_HORIZON_END_ISO}
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className="min-h-11 w-full rounded-control border border-line-strong py-2 pr-10 pl-2.5 text-small [&::-webkit-calendar-picker-indicator]:hidden"
                  />
                  <button
                    type="button"
                    onClick={() => startDateInputRef.current?.showPicker?.()}
                    aria-label="Open start date picker"
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-ink-muted"
                  >
                    <CalendarIcon />
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="leave-end-date" className="mb-1 block text-caption font-bold text-ink-muted">
                  End date
                </label>
                <div className="relative">
                  <input
                    id="leave-end-date"
                    ref={endDateInputRef}
                    type="date"
                    min={REAL_TODAY_ISO}
                    max={REQUEST_HORIZON_END_ISO}
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className="min-h-11 w-full rounded-control border border-line-strong py-2 pr-10 pl-2.5 text-small [&::-webkit-calendar-picker-indicator]:hidden"
                  />
                  <button
                    type="button"
                    onClick={() => endDateInputRef.current?.showPicker?.()}
                    aria-label="Open end date picker"
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-ink-muted"
                  >
                    <CalendarIcon />
                  </button>
                </div>
              </div>
            </div>
            {startDateInPast && (
              <p className="text-caption font-semibold text-error">Start date can&apos;t be before today.</p>
            )}
            {beyondRequestHorizon && (
              <p className="text-caption font-semibold text-error">
                Leave can&apos;t be requested past {REQUEST_HORIZON_END_LABEL}, the end of the request horizon.
              </p>
            )}
            {dateRangeInvalid && (
              <p className="text-caption font-semibold text-error">End date must be on or after the start date.</p>
            )}

            <fieldset>
              <legend className="mb-1 block text-caption font-bold text-ink-muted">Full day or partial day</legend>
              <div className="flex gap-2">
                <label
                  className={`flex min-h-11 flex-1 cursor-pointer items-center justify-center gap-2 rounded-control border px-3 text-small font-bold ${
                    fullDay ? 'border-primary bg-selected' : 'border-line-strong'
                  }`}
                >
                  <input
                    type="radio"
                    name="leave-day-length"
                    checked={fullDay}
                    onChange={() => setFullDay(true)}
                    className="size-4.5 accent-primary"
                  />
                  Full day
                </label>
                <label
                  className={`flex min-h-11 flex-1 cursor-pointer items-center justify-center gap-2 rounded-control border px-3 text-small font-bold ${
                    !fullDay ? 'border-primary bg-selected' : 'border-line-strong'
                  }`}
                >
                  <input
                    type="radio"
                    name="leave-day-length"
                    checked={!fullDay}
                    onChange={() => setFullDay(false)}
                    className="size-4.5 accent-primary"
                  />
                  Partial day
                </label>
              </div>
            </fieldset>

            {!fullDay && (
              <div className="flex flex-col gap-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p id="leave-start-time-label" className="mb-1 block text-caption font-bold text-ink-muted">
                      Start time
                    </p>
                    <QuarterHourField label="Start time" id="leave-start-time" value={startTime} onChange={setStartTime} />
                  </div>
                  <div>
                    <p id="leave-end-time-label" className="mb-1 block text-caption font-bold text-ink-muted">
                      End time
                    </p>
                    <QuarterHourField label="End time" id="leave-end-time" value={endTime} onChange={setEndTime} />
                  </div>
                </div>
                {timeRangeInvalid && (
                  <p className="text-caption font-semibold text-error">End time must be after the start time.</p>
                )}
              </div>
            )}

            <fieldset>
              <legend className="mb-1 block text-caption font-bold text-ink-muted">Leave type</legend>
              <div className="flex flex-col gap-2">
                {leaveTypes.map((type) => (
                  <label
                    key={type}
                    className={`flex min-h-11 cursor-pointer items-center gap-3 rounded-control border px-3 text-small font-bold ${
                      leaveType === type ? 'border-primary bg-selected' : 'border-line-strong'
                    }`}
                  >
                    <input
                      type="radio"
                      name="leave-type"
                      checked={leaveType === type}
                      onChange={() => setChosenLeaveType(type)}
                      className="size-4.5 accent-primary"
                    />
                    {type}
                  </label>
                ))}
              </div>
            </fieldset>

            <div>
              <label htmlFor="leave-comment" className="mb-1 block text-caption font-bold text-ink-muted">
                Comment <span className="font-normal text-ink-subtle">(optional)</span>
              </label>
              <textarea
                id="leave-comment"
                value={comment}
                onChange={(event) => setComment(event.target.value.slice(0, COMMENT_MAX_LENGTH))}
                rows={3}
                className="w-full rounded-control border border-line-strong p-2.5 text-small"
              />
              <p className="mt-1 text-caption text-ink-subtle">
                {comment.length}/{COMMENT_MAX_LENGTH}
              </p>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 flex-none border-t border-line bg-canvas p-4 pb-[max(1rem,env(safe-area-inset-bottom))] lg:p-6">
          <div className="mx-auto w-full max-w-2xl">
            {submitFailed && (
              <p role="alert" className="mb-2 text-caption font-semibold text-error">
                {reopenTarget
                  ? reopenTarget.state === 'pending'
                    ? 'Could not save the changes. The request may already have been decided; check the request history.'
                    : 'Could not resubmit the request. Try again or use a different browser.'
                  : 'Could not submit the request. Storage may be unavailable in this browser; try again or use a different browser.'}
              </p>
            )}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="w-full rounded-control bg-primary px-4 py-3.5 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
            >
              {reopenTarget ? (reopenTarget.state === 'pending' ? 'Save Changes' : 'Resubmit Request') : 'Submit Request'}
            </button>
            {reopenTarget?.state === 'pending' &&
              (confirmingCancel ? (
                <div className="mt-3 rounded-surface border border-line bg-surface-2 p-3">
                  <p className="text-small font-bold">Cancel this leave request?</p>
                  <p className="mt-1 text-small text-ink-muted">Your supervisor will no longer see it.</p>
                  {cancelFailed && (
                    <p role="alert" className="mt-2 text-caption font-semibold text-error">
                      Could not cancel the request. It may already have been decided; check the request history.
                    </p>
                  )}
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="min-h-11 flex-1 rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover"
                    >
                      Yes, cancel request
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmingCancel(false)}
                      className="min-h-11 flex-1 rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2"
                    >
                      Keep request
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmingCancel(true)}
                  className="mt-2 min-h-11 w-full rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2"
                >
                  Cancel Request
                </button>
              ))}
          </div>
        </div>
      </main>
    </div>
  )
}
